import React, { useState, useEffect } from 'react';
import { getSwedenHolidaysForYear, getHolidayName } from '../utils/swedishHolidays';

function CalendarComponent({
  month,
  year,
  myAvailabilities,
  availabilityByDate,
  disabledDates,
  onToggleDate,
  onAdminToggleDate,
  adminDisableMode,
  inspectMode,
  selectedDateForInspect,
  onSelectDateForInspect
}) {
  const [swedishHolidays, setSwedsishHolidays] = useState([]);
  const [holidayNames, setHolidayNames] = useState({});

  // Fetch Swedish holidays on mount and when year changes
  useEffect(() => {
    const fetchHolidays = async () => {
      const holidays = await getSwedenHolidaysForYear(year);
      setSwedsishHolidays(holidays || []);
    };
    fetchHolidays();
  }, [year]);

  const getHolidayNameCached = async (dateStr) => {
    if (holidayNames[dateStr]) {
      return holidayNames[dateStr];
    }
    const name = await getHolidayName(dateStr);
    setHolidayNames(prev => ({ ...prev, [dateStr]: name }));
    return name;
  };

  const isHoliday = (dateKey) => swedishHolidays.includes(dateKey);

  const isWeekend = (day) => {
    if (!day) return false;
    const dayOfWeek = new Date(year, month - 1, day).getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  };

  const getDaysInMonth = (m, y) => new Date(y, m, 0).getDate();
  // getDay() returns 0=Sun..6=Sat; shift so Monday=0, Sunday=6
  const getFirstDayOfMonth = (m, y) => (new Date(y, m - 1, 1).getDay() + 6) % 7;

  const daysInMonth = getDaysInMonth(month, year);
  const firstDay = getFirstDayOfMonth(month, year);
  const days = [];

  // Add empty cells
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }

  // Add days
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  const getDateStatus = (day) => {
    if (!day) return null;

    const dateKey = getDateKey(day);

    const isDisabled = disabledDates.some((dd) => {
      const disabledKey = new Date(dd.date).toISOString().split('T')[0];
      return disabledKey === dateKey;
    });

    if (isDisabled) return 'disabled';

    const isAvailable = myAvailabilities.some((av) => {
      const avKey = new Date(av.date).toISOString().split('T')[0];
      return avKey === dateKey && av.available;
    });

    return isAvailable ? 'available' : 'unavailable';
  };

  const getDateKey = (day) => {
    if (!day) return null;
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.toISOString().split('T')[0];
  };

  const isPastDate = (day) => {
    if (!day) return false;
    const dateKey = getDateKey(day);
    const today = new Date();
    const todayKey = today.toISOString().split('T')[0];
    return dateKey < todayKey;
  };

  const handleDayClick = (day) => {
    if (!day || isPastDate(day)) return;

    if (inspectMode) {
      const dateKey = getDateKey(day);
      onSelectDateForInspect(selectedDateForInspect === dateKey ? null : dateKey);
      return;
    }

    const dateKey = getDateKey(day);
    const status = getDateStatus(day);

    if (adminDisableMode && onAdminToggleDate) {
      onAdminToggleDate(dateKey, status === 'disabled');
      return;
    }

    if (status === 'disabled') return;
    onToggleDate(dateKey);
  };

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="calendar-grid">
      {dayNames.map((name) => (
        <div key={name} className="calendar-day-header">
          {name}
        </div>
      ))}
      {days.map((day, index) => {
        const status = getDateStatus(day);
        const dayUsers = day ? (availabilityByDate[getDateKey(day)] || []) : [];
        const visibleUsers = dayUsers.slice(0, 10);
        const hiddenCount = dayUsers.length - visibleUsers.length;
        const dayTooltip = dayUsers
          .map((u) => u.username)
          .join(', ');

         return (
           <div
             key={index}
              className={`calendar-day ${day ? status : 'empty'} ${isPastDate(day) ? 'past' : ''} ${day && isHoliday(getDateKey(day)) ? 'holiday' : ''} ${isWeekend(day) ? 'weekend' : ''} ${day && selectedDateForInspect === getDateKey(day) ? 'selected' : ''} ${adminDisableMode ? 'admin-mode' : ''}`}
              onClick={() => handleDayClick(day)}
              onMouseEnter={() => {
                if (day && isHoliday(getDateKey(day))) {
                  getHolidayNameCached(getDateKey(day));
                }
              }}
              title={day && holidayNames[getDateKey(day)] ? holidayNames[getDateKey(day)] : ''}
            >
             {day && (
               <>
                 <span className="day-number">{day}
                 {isHoliday(getDateKey(day)) && (
                   <span className="holiday-badge" title={holidayNames[getDateKey(day)] || 'Swedish Holiday'}>🇸🇪</span>
                 )}
                   </span>
                <div className="day-dots" title={dayTooltip}>
                  {visibleUsers.map((user) => (
                    <span
                      key={user.userId}
                      className="user-dot"
                      style={{ backgroundColor: user.color }}
                      title={user.username}
                    ></span>
                  ))}
                  {hiddenCount > 0 && <span className="more-dots">+{hiddenCount}</span>}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default CalendarComponent;

